import { Context } from '@nuxt/types'

export interface AuthCookieAttributes {
  name: string
  value: string
  expires?: Date
  maxAge?: number
  domain?: string
  path?: string
  secure?: boolean
  httpOnly?: boolean
  sameSite?: 'Strict' | 'Lax' | 'None'
}

/**
 * Parses Set-Cookie headers from HTTP responses into a simple key-value object.
 *
 * This function handles:
 * - Single or multiple Set-Cookie headers
 * - Cookie attributes (ignoring them)
 * - Malformed headers (ignoring them)
 * - Empty or undefined input
 *
 * @param setCookieHeaders - The Set-Cookie header(s) from an HTTP response.
 *                          Can be a single string, array of strings, or undefined.
 * @returns A record of cookie names to their values. Only includes cookies that
 *          have both a name and a value. Malformed headers and cookies with
 *          empty values are ignored.
 *
 * @example
 * // Single header
 * parseSetCookieHeaders('sessionId=abc123; Path=/; HttpOnly')
 * // => { sessionId: 'abc123' }
 *
 * // Multiple headers
 * parseSetCookieHeaders(['sessionId=abc123', 'userId=xyz789'])
 * // => { sessionId: 'abc123', userId: 'xyz789' }
 *
 * // Malformed header
 * parseSetCookieHeaders('invalid-header')
 * // => {}
 */
export const parseSetCookieHeaders = (
  setCookieHeaders: string | string[] | undefined
): Record<string, AuthCookieAttributes> => {
  if (!setCookieHeaders) return {}

  // Convert single header to array for consistent processing
  const headers = Array.isArray(setCookieHeaders) ? setCookieHeaders : [setCookieHeaders]

  return headers.reduce((parsedCookies: Record<string, AuthCookieAttributes>, header) => {
    const parts = header.split(';').map((part) => part.trim())
    const [nameValue, ...attributes] = parts

    // Skip malformed headers that don't contain an equals sign
    if (!nameValue.includes('=')) {
      return parsedCookies
    }

    const [cookieName, cookieValue] = nameValue.split('=')

    // Only include cookies that have both a name and a value
    if (cookieName && cookieValue) {
      const cookie: AuthCookieAttributes = {
        name: cookieName,
        value: cookieValue
      }

      // Parse attributes
      attributes.forEach((attr) => {
        const [attrName, attrValue] = attr.split('=')
        const lowerAttrName = attrName.toLowerCase()

        switch (lowerAttrName) {
          case 'expires': {
            const date = new Date(attrValue)
            if (!isNaN(date.getTime())) {
              cookie.expires = date
            }
            break
          }
          case 'max-age': {
            const maxAge = parseInt(attrValue, 10)
            if (!isNaN(maxAge)) {
              cookie.maxAge = maxAge
            }
            break
          }
          case 'domain':
            cookie.domain = attrValue
            break
          case 'path':
            cookie.path = attrValue
            break
          case 'secure':
            cookie.secure = true
            break
          case 'httponly':
            cookie.httpOnly = true
            break
          case 'samesite':
            if (['Strict', 'Lax', 'None'].includes(attrValue)) {
              cookie.sameSite = attrValue as 'Strict' | 'Lax' | 'None'
            }
            break
        }
      })

      parsedCookies[cookieName] = cookie
    }

    return parsedCookies
  }, {})
}

/**
 * Determines the appropriate CSRF endpoint URI based on the current environment and context.
 *
 * This function handles three main scenarios:
 * 1. Development environment - returns localhost endpoint
 * 2. Server-side rendering - returns server-specific endpoint
 * 3. Client-side rendering - returns client-specific endpoint
 *
 * @param config - The Nuxt configuration object containing CSRF endpoint URIs
 * @returns The appropriate CSRF endpoint URI for the current context
 * @throws {Error} If:
 *   - The server URI is not configured in server context
 *   - The client URI is not configured in client context
 *   - Neither server nor client context is detected
 *
 * @example
 * // In development
 * getCsrfEndpoint(config) // returns 'http://localhost:8000/api/set-csrf-cookie/'
 *
 * // In production server context
 * getCsrfEndpoint(config) // returns config.csrfServerUri
 *
 * // In production client context
 * getCsrfEndpoint(config) // returns config.csrfClientUri
 */
export const getCsrfEndpoint = (config: Context['$config']): string => {
  if (process.server) {
    if (!config.csrfServerUri) {
      throw new Error('CSRF server URI is not configured')
    }
    return config.csrfServerUri
  }

  if (process.client) {
    if (!config.csrfClientUri) {
      throw new Error('CSRF client URI is not configured')
    }
    return config.csrfClientUri
  }

  throw new Error('Unable to determine CSRF URI - neither server nor client context detected')
}

/**
 * Retrieves a CSRF token, either from existing cookies or by making a request to the CSRF endpoint.
 *
 * This function:
 * - First checks for an existing CSRF token in the provided cookies
 * - If no token exists, makes a request to the CSRF endpoint to obtain one
 * - Parses the Set-Cookie header from the response to extract the token
 * - Validates that a token was successfully obtained
 *
 * @param cookies - A record of existing cookies to check for a CSRF token
 * @param csrfUri - The URI of the CSRF endpoint to request a new token from if needed
 * @returns A promise that resolves to the CSRF token string
 * @throws {Error} If:
 *   - No Set-Cookie header is present in the response
 *   - No CSRF token is found in the Set-Cookie header
 *
 * @example
 * // With existing token
 * const token = await getCsrfToken({ csrftoken: 'abc123' }, 'https://api.example.com/csrf')
 * // => 'abc123'
 *
 * // Without existing token
 * const token = await getCsrfToken({}, 'https://api.example.com/csrf')
 * // => 'new-token-from-server'
 */
export const getCsrfToken = async (
  cookies: Record<string, string>,
  csrfUri: string
): Promise<string> => {
  let csrfToken = cookies.csrftoken
  if (!csrfToken) {
    const response = await fetch(csrfUri, {
      credentials: 'include'
    })
    const cookieString = response.headers.get('set-cookie')
    if (!cookieString) {
      throw new Error('No CSRF token found in response')
    }
    const parsedCookies = parseSetCookieHeaders(cookieString)
    csrfToken = parsedCookies.csrftoken?.value
    if (!csrfToken) {
      throw new Error('No CSRF token found in response')
    }
  }
  return csrfToken
}

/**
 * Decodes and validates a JWT token, extracting its payload.
 *
 * This function handles:
 * - Token format validation (header.payload.signature)
 * - Base64 decoding of the payload
 * - JSON parsing of the decoded payload
 * - Validation of required claims (exp)
 * - Environment-specific base64 decoding (client/server)
 *
 * @param token - The JWT token to decode
 * @returns The decoded and validated JWT payload
 * @throws {Error} If:
 *   - Token is missing or empty
 *   - Token format is invalid (not 3 parts)
 *   - Payload is missing
 *   - Payload is not valid base64 encoded JSON
 *   - Decoded payload is not a valid object
 *   - Required 'exp' claim is missing or invalid
 *
 * @example
 * // Valid token
 * const payload = getJWTPayload('eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJleHAiOjE2MzQ1Njc4OTAsInN1YiI6IjEyMzQ1Njc4OTAifQ.signature')
 * // => { exp: 1634567890, sub: '1234567890' }
 *
 * // Invalid token
 * getJWTPayload('invalid.token')
 * // => throws Error: Invalid token format: must have 3 parts
 */
export function getJWTPayload(token: string) {
  if (!token) {
    throw new Error('Token is required')
  }

  const parts = token.split('.')
  if (parts.length !== 3) {
    throw new Error('Invalid token format: must have 3 parts')
  }

  const payload = parts[1]
  if (!payload) {
    throw new Error('Invalid token: missing payload')
  }

  try {
    // Use browser's atob in client, Node's atob in server
    const decodeStr = process.client ? atob : require('atob')
    const decodedToken = JSON.parse(decodeStr(payload))

    if (!decodedToken || typeof decodedToken !== 'object') {
      throw new Error('Invalid token: payload must be a valid JSON object')
    }

    if (typeof decodedToken.exp !== 'number') {
      throw new Error('Invalid token: missing or invalid expiration time')
    }

    return decodedToken
  } catch (error) {
    if (error instanceof SyntaxError) {
      throw new Error('Invalid token: payload is not valid base64 encoded JSON')
    }
    throw error
  }
}

/**
 * Checks if a JWT token is expired.
 *
 * This function:
 * - Decodes the token using getJWTPayload
 * - Adds a 30-second buffer to account for clock skew and network latency
 * - Returns true if the token is expired or invalid
 *
 * @param token - The JWT token to check
 * @returns true if the token is expired or invalid, false otherwise
 *
 * @example
 * // Valid, non-expired token
 * isJWTExpired('eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJleHAiOjE2MzQ1Njc4OTAsInN1YiI6IjEyMzQ1Njc4OTAifQ.signature')
 * // => false
 *
 * // Expired token
 * isJWTExpired('eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJleHAiOjE2MzQ1Njc4OTAsInN1YiI6IjEyMzQ1Njc4OTAifQ.signature')
 * // => true
 */
export const isJWTExpired = (token: string): boolean => {
  try {
    const { exp } = getJWTPayload(token)
    const now = (new Date().getTime() + 30_000) / 1000
    return now > exp
  } catch (error) {
    // If we can't parse the token or it's invalid, consider it expired
    return true
  }
}

/**
 * Appends a cookie to an existing cookie string or creates a new cookie string.
 *
 * This function:
 * - Handles both empty and non-empty existing cookie strings
 * - Properly formats the cookie with name=value syntax
 * - Maintains proper cookie string formatting with semicolon separators
 *
 * @param currentCookie - The existing cookie string to append to, or an empty string
 * @param cookieName - The name of the cookie to add
 * @param cookieValue - The value of the cookie to add
 * @returns A properly formatted cookie string
 *
 * @example
 * // Adding to existing cookies
 * appendCookieString('session=abc123', 'user', 'john')
 * // => 'session=abc123; user=john'
 *
 * // Creating new cookie string
 * appendCookieString('', 'session', 'abc123')
 * // => 'session=abc123'
 */
export const appendCookieString = (
  currentCookie: string,
  cookieName: string,
  cookieValue: string
): string => {
  if (!cookieName || !cookieValue) {
    return currentCookie
  }

  const newCookie = `${cookieName}=${cookieValue}`
  return currentCookie ? `${currentCookie}; ${newCookie}` : newCookie
}

/**
 * Generates a Set-Cookie header string from a cookie object.
 *
 * @param cookie - The cookie object containing name, value, and attributes
 * @returns A properly formatted Set-Cookie header string
 *
 * @example
 * const cookie = {
 *   name: 'JWT',
 *   value: 'token123',
 *   httpOnly: true,
 *   secure: true,
 *   sameSite: 'Strict',
 *   path: '/'
 * }
 * generateSetCookieString(cookie)
 * // => 'JWT=token123; HttpOnly; Secure; SameSite=Strict; Path=/'
 */
export const generateSetCookieString = (cookie: AuthCookieAttributes): string => {
  const parts = [`${cookie.name}=${cookie.value}`]

  if (cookie.httpOnly) parts.push('HttpOnly')
  if (cookie.secure) parts.push('Secure')
  if (cookie.sameSite) parts.push(`SameSite=${cookie.sameSite}`)
  if (cookie.path) parts.push(`Path=${cookie.path}`)
  if (cookie.domain) parts.push(`Domain=${cookie.domain}`)
  if (cookie.expires) parts.push(`Expires=${cookie.expires.toUTCString()}`)
  if (cookie.maxAge) parts.push(`Max-Age=${cookie.maxAge}`)

  return parts.join('; ')
}
